import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * What a broken screen says instead of nothing.
 *
 * React unmounts the entire tree when a render throws, so before this a single
 * bad value produced a completely black page — no header, no message, nothing
 * in the interface to photograph. Diagnosing that from the outside is
 * guesswork, and I spent a round of it guessing.
 *
 * Now the page says what threw and where, which is one screenshot instead of
 * an afternoon.
 */

type Props = { children: ReactNode; where?: string };
type State = { error: Error | null; stack: string | null };

/**
 * A deploy replaces the hashed chunk files. A tab left open still holds the
 * old index.html, so its next lazy import fetches a file that has gone.
 * Reloading once picks up the new one; the flag stops that becoming a loop.
 */
const STALE = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const RELOAD_KEY = 'ads-chunk-reload';

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, stack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (STALE.test(error.message)) {
      let already = '0';
      try { already = sessionStorage.getItem(RELOAD_KEY) ?? '0'; } catch { /* private mode */ }
      if (already !== '1') {
        try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch { /* private mode */ }
        window.location.reload();
        return;
      }
    }
    console.error(`[${this.props.where ?? 'app'}]`, error, info.componentStack);
    this.setState({ stack: info.componentStack ?? null });
  }

  render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    // The first few frames of the component stack name the component that
    // threw, which is the whole diagnosis in one line.
    const frames = (stack ?? '').trim().split('\n').slice(0, 6).join('\n');

    return (
      <div className="m-6 rounded-card border border-bd bg-bg2 p-6">
        <h2 className="text-base font-bold">This screen could not be drawn</h2>
        <p className="mt-1 max-w-prose text-sm text-t2">
          Nothing was lost — the data is fine, this is the page itself. Send this whole box to whoever is building it.
        </p>
        <pre className="mt-3 max-h-64 overflow-auto rounded-btn bg-bg3 p-3 text-xs text-err">
          {this.props.where ? `at ${this.props.where}\n\n` : ''}
          {error.name}: {error.message}
          {frames ? `\n${frames}` : ''}
        </pre>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => { try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ } window.location.reload(); }}
            className="h-9 rounded-btn bg-ink px-4 text-sm font-medium text-on-ink"
          >
            Reload
          </button>
          <button
            onClick={() => navigator.clipboard?.writeText(`${error.name}: ${error.message}\n${frames}`)}
            className="h-9 rounded-btn border border-bd px-4 text-sm"
          >
            Copy the error
          </button>
        </div>
      </div>
    );
  }
}
